import React, { useState } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Table,
  Pagination,
  Spinner,
} from 'react-bootstrap';
import {
  FaSearch,
  FaFileExcel,
  FaTimes,
  FaExclamationCircle,
} from 'react-icons/fa';
import moment from 'moment';
import { toast } from 'react-toastify';
import Header from '~/Components/Header/Header';
import Loading from '~/Components/Loading';
import { consultarContasSaldoDisponivel } from '~/Services/Service';
import { salvarEmExcel } from '~/Utils/Helpers';

const REGISTROS_POR_PAGINA = 20;

const formatarMoeda = (valor) => {
  if (valor == null) return '-';
  return parseFloat(valor).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
};

const formatarData = (valor) => {
  if (!valor) return '-';
  return moment(valor).format('DD/MM/YYYY');
};

const ContasSaldoDisponivel = () => {
  const [dataInicial, setDataInicial] = useState(moment().format('YYYY-MM-DD'));
  const [dataFinal, setDataFinal] = useState(moment().format('YYYY-MM-DD'));
  const [itens, setItens] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [consultado, setConsultado] = useState(false);
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [exportando, setExportando] = useState(false);

  const totalPaginas = Math.ceil(itens.length / REGISTROS_POR_PAGINA);

  const itensOrdenados = React.useMemo(() => {
    if (!sortConfig.key) return itens;
    return [...itens].sort((a, b) => {
      const aVal = a[sortConfig.key] != null ? a[sortConfig.key] : '';
      const bVal = b[sortConfig.key] != null ? b[sortConfig.key] : '';
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [itens, sortConfig]);

  const itensPagina = React.useMemo(() => {
    const inicio = (paginaAtual - 1) * REGISTROS_POR_PAGINA;
    return itensOrdenados.slice(inicio, inicio + REGISTROS_POR_PAGINA);
  }, [itensOrdenados, paginaAtual]);

  const handleSort = (coluna) => {
    setSortConfig((prev) => ({
      key: coluna,
      direction:
        prev.key === coluna && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const sortIcon = (coluna) => {
    if (sortConfig.key !== coluna) return ' ↕';
    return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
  };

  const datasValidas = () => {
    if (!dataInicial || !dataFinal) return false;
    return dataInicial <= dataFinal;
  };

  const handleConsultar = async () => {
    setCarregando(true);
    try {
      const res = await consultarContasSaldoDisponivel(dataInicial, dataFinal);
      const lista = res && res.itens ? res.itens : [];
      setItens(lista);
      setConsultado(true);
      setPaginaAtual(1);
    } catch (err) {
      toast.error(
        err && err.message ? err.message : 'Erro ao consultar dados'
      );
    } finally {
      setCarregando(false);
    }
  };

  const handleLimpar = () => {
    setDataInicial('');
    setDataFinal('');
    setItens([]);
    setConsultado(false);
    setPaginaAtual(1);
  };

  const handleExportarExcel = async () => {
    if (!itensOrdenados.length) {
      toast.warning('Nenhum dado para exportar.');
      return;
    }
    try {
      setExportando(true);
      await salvarEmExcel(
        itensOrdenados,
        '/exportador/excel/contas-saldo-disponivel'
      );
    } catch (err) {
      toast.error(
        err && err.message ? err.message : 'Erro ao exportar Excel.'
      );
    } finally {
      setExportando(false);
    }
  };

  const renderPagination = () => {
    const items = [];
    const maxVisible = 5;
    const start = Math.max(1, paginaAtual - Math.floor(maxVisible / 2));
    const end = Math.min(totalPaginas, start + maxVisible - 1);

    if (start > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => setPaginaAtual(1)}>
          1
        </Pagination.Item>
      );
      if (start > 2) {
        items.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);
      }
    }

    for (let p = start; p <= end; p++) {
      items.push(
        <Pagination.Item
          key={p}
          active={p === paginaAtual}
          onClick={() => setPaginaAtual(p)}
        >
          {p}
        </Pagination.Item>
      );
    }

    if (end < totalPaginas) {
      if (end < totalPaginas - 1) {
        items.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);
      }
      items.push(
        <Pagination.Item
          key={totalPaginas}
          onClick={() => setPaginaAtual(totalPaginas)}
        >
          {totalPaginas}
        </Pagination.Item>
      );
    }

    return items;
  };

  const primeiroReg = (paginaAtual - 1) * REGISTROS_POR_PAGINA + 1;
  const ultimoReg = Math.min(paginaAtual * REGISTROS_POR_PAGINA, itens.length);

  return (
    <div>
      <Header title="Contas Saldo Disponível" />

      <div className="container-fluid bg-white p-3">
        <Row>
          <Col xs={12} sm={6} md={3}>
            <Form.Group>
              <Form.Label>Data Inicial</Form.Label>
              <Form.Control
                type="date"
                value={dataInicial}
                onChange={(e) => {
                  setDataInicial(e.target.value);
                  setConsultado(false);
                }}
              />
            </Form.Group>
          </Col>
          <Col xs={12} sm={6} md={3}>
            <Form.Group>
              <Form.Label>Data Final</Form.Label>
              <Form.Control
                type="date"
                value={dataFinal}
                onChange={(e) => {
                  setDataFinal(e.target.value);
                  setConsultado(false);
                }}
              />
            </Form.Group>
          </Col>
          <Col xs={12} sm={12} md={6} className="d-flex align-items-end pb-3">
            <Button
              variant="primary"
              className="mr-2"
              onClick={handleConsultar}
              disabled={carregando || !datasValidas()}
            >
              {carregando ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="mr-2"
                  />
                  Consultando...
                </>
              ) : (
                <>
                  <FaSearch className="mr-2" />
                  Consultar
                </>
              )}
            </Button>
            <Button
              variant="secondary"
              className="mr-2"
              onClick={handleLimpar}
              disabled={carregando}
            >
              <FaTimes className="mr-2" />
              Limpar
            </Button>
            <Button
              variant="outline-success"
              onClick={handleExportarExcel}
              disabled={itens.length === 0 || exportando}
            >
              {exportando ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="mr-2"
                  />
                  Exportando...
                </>
              ) : (
                <>
                  <FaFileExcel className="mr-2" />
                  Exportar Excel
                </>
              )}
            </Button>
          </Col>
        </Row>
      </div>

      {carregando && <Loading />}

      {!carregando && itens.length > 0 && (
        <>
          <div className="container-fluid bg-white p-3 mt-3">
            <Row>
              <Col>
                <span>Total de {itens.length} registro(s) encontrado(s)</span>
              </Col>
            </Row>
          </div>
          <div className="container-fluid bg-white table-responsive">
            <Table striped bordered hover size="sm">
              <thead>
                <tr>
                  <th
                    onClick={() => handleSort('codigo_grupo_empresa')}
                    style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                  >
                    GE{sortIcon('codigo_grupo_empresa')}
                  </th>
                  <th
                    onClick={() => handleSort('codigo_conta')}
                    style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                  >
                    Conta{sortIcon('codigo_conta')}
                  </th>
                  <th
                    onClick={() => handleSort('apelido_conta')}
                    style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                  >
                    Descrição{sortIcon('apelido_conta')}
                  </th>
                  <th
                    onClick={() => handleSort('data_mata')}
                    style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                  >
                    Data{sortIcon('data_mata')}
                  </th>
                  <th
                    onClick={() => handleSort('disponivel')}
                    style={{
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      textAlign: 'right',
                    }}
                  >
                    Disponível{sortIcon('disponivel')}
                  </th>
                  <th
                    onClick={() => handleSort('cdb')}
                    style={{
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      textAlign: 'right',
                    }}
                  >
                    CDB{sortIcon('cdb')}
                  </th>
                  <th
                    onClick={() => handleSort('aplicacao_financeira')}
                    style={{
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      textAlign: 'right',
                    }}
                  >
                    Aplic. Financeira{sortIcon('aplicacao_financeira')}
                  </th>
                  <th
                    onClick={() => handleSort('total')}
                    style={{
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      textAlign: 'right',
                    }}
                  >
                    Total{sortIcon('total')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {itensPagina.map((item) => (
                  <tr
                    key={`${item.codigo_grupo_empresa}-${item.codigo_conta}-${item.data_mata}`}
                  >
                    <td>{item.codigo_grupo_empresa}</td>
                    <td>{item.codigo_conta}</td>
                    <td>{item.apelido_conta}</td>
                    <td>{formatarData(item.data_mata)}</td>
                    <td style={{ textAlign: 'right' }}>
                      {formatarMoeda(item.disponivel)}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {item.cdb !== 0 ? formatarMoeda(item.cdb) : ''}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {item.aplicacao_financeira !== 0
                        ? formatarMoeda(item.aplicacao_financeira)
                        : ''}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {item.total !== 0 ? formatarMoeda(item.total) : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          <div className="container-fluid bg-white p-3">
            <Row className="mt-2 d-flex justify-content-between align-items-center">
              <Col xs="auto" className="d-flex align-items-center flex-wrap">
                <span
                  className="text-muted mr-3"
                  style={{ whiteSpace: 'nowrap' }}
                >
                  Mostrando {primeiroReg} até {ultimoReg} de {itens.length}{' '}
                  registros
                </span>
                {totalPaginas > 1 && (
                  <Pagination className="d-inline-flex mb-0">
                    <Pagination.First
                      onClick={() => setPaginaAtual(1)}
                      disabled={paginaAtual === 1}
                    />
                    <Pagination.Prev
                      onClick={() => setPaginaAtual(paginaAtual - 1)}
                      disabled={paginaAtual === 1}
                    />
                    {renderPagination()}
                    <Pagination.Next
                      onClick={() => setPaginaAtual(paginaAtual + 1)}
                      disabled={paginaAtual === totalPaginas}
                    />
                    <Pagination.Last
                      onClick={() => setPaginaAtual(totalPaginas)}
                      disabled={paginaAtual === totalPaginas}
                    />
                  </Pagination>
                )}
              </Col>
            </Row>
          </div>
        </>
      )}

      {!carregando && consultado && itens.length === 0 && (
        <div className="container-fluid bg-white p-5 mt-3 text-center">
          <Row>
            <Col>
              <FaExclamationCircle size={48} className="text-muted mb-3" />
              <h5 className="text-muted">Nenhuma informação encontrada</h5>
              <p className="text-muted">
                Nenhuma informação encontrada para o período informado.
              </p>
            </Col>
          </Row>
        </div>
      )}
    </div>
  );
};

export default ContasSaldoDisponivel;
